/**
 * Canonical oilfield commodity colors — ONE source of truth app-wide.
 *
 * Oil / Gas / Water must match across:
 *   Active Ops type mix · pipeline veins · map chips · pins · FABs
 *
 * Palette (dark-map friendly):
 *   Oil   — amber / black-gold family (crude · “black gold” = oil)
 *   Gas   — green flame (gas wells / gas lines · Gas Flare chrome)
 *   Water — sky    (water wells, produced-water lines, SWD family)
 *
 * Industry identity: **black gold is oil**. Map pins stay amber-gold for
 * readability on satellite; chrome theme “Black Gold” is the full UI look.
 * Do not invent one-off hex for these three commodities elsewhere — import here.
 */

export type OilfieldCommodity = 'oil' | 'gas' | 'water' | 'swd';

export type CommodityColorTokens = {
  /** Primary hex used on map lines, chips, icons */
  main: string;
  /** Brighter variant (dark UI bars / glow) */
  bright: string;
  /** Deeper variant (light UI bars / text on pale bg) */
  deep: string;
  label: string;
};

/**
 * Shared tokens. `swd` aliases water so disposal never drifts from the water family.
 */
export const OILFIELD_COMMODITY_COLORS: Record<OilfieldCommodity, CommodityColorTokens> = {
  oil: {
    main: '#f59e0b',
    bright: '#fbbf24',
    deep: '#d97706',
    label: 'Oil',
  },
  gas: {
    main: '#22c55e',
    bright: '#4ade80',
    deep: '#16a34a',
    label: 'Gas',
  },
  water: {
    main: '#0ea5e9',
    bright: '#38bdf8',
    deep: '#0284c7',
    label: 'Water',
  },
  // Same family as water — SWD is produced-water disposal, not a fourth random hue
  swd: {
    main: '#0ea5e9',
    bright: '#38bdf8',
    deep: '#0284c7',
    label: 'SWD',
  },
};

/** Map/chip/pin primary color for a commodity. */
export function commodityColor(commodity: OilfieldCommodity): string {
  return OILFIELD_COMMODITY_COLORS[commodity].main;
}

/** Bar / filter chip color that stays readable in dark or light chrome. */
export function commodityColorForMode(commodity: OilfieldCommodity, darkMode: boolean): string {
  const t = OILFIELD_COMMODITY_COLORS[commodity];
  return darkMode ? t.bright : t.deep;
}

/** Soft fill (e.g. chip bg) */
export function commodityColorAlpha(commodity: OilfieldCommodity, alpha = 0.18): string {
  const hex = commodityColor(commodity).replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.slice(0, 6);
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return commodityColor(commodity);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
